/**
 * chatStream.ts — Firestore-backed chat streaming.
 *
 * Instead of streaming AI text directly over HTTP (fragile — drops on network
 * blips, mobile background, proxy timeouts), the backend writes partial text
 * to a Firestore doc and the frontend subscribes for real-time updates.
 *
 * Data model:
 *   chatStreams/{streamId} → { text, media?, done, error, model, uid, createdAt, updatedAt }
 */

import { doc, onSnapshot, Unsubscribe } from "firebase/firestore";
import { db } from "./config";
import { authHeaders } from "./authHeaders";
import { GeneratedMediaItem } from "../types/media";

const CHAT_STREAM_URL =
  "https://chatwithaistream-mt62q3wpaa-uc.a.run.app/chatWithAIStream";

export interface ChatStreamCallbacks {
  onText: (text: string) => void;
  onDone: (text: string, model: string, media?: GeneratedMediaItem[]) => void;
  onError: (error: string) => void;
}

/**
 * Start a Firestore-backed chat stream.
 *
 * 1. POST to backend → get back { streamId } on the first NDJSON line
 * 2. Subscribe to chatStreams/{streamId} via onSnapshot
 * 3. Relay partial text + done/error signals to the caller
 *
 * Returns an unsubscribe function to tear down the Firestore listener.
 */
export async function startChatStream(
  params: {
    messages: any[];
    model: string;
    uid: string | null;
    email?: string | null;
    generationMode?: "text" | "image" | "video";
  },
  callbacks: ChatStreamCallbacks,
): Promise<Unsubscribe> {
  // 1. Kick off the backend request
  const resp = await fetch(CHAT_STREAM_URL, {
    method: "POST",
    headers: await authHeaders(),
    body: JSON.stringify(params),
  });

  if (!resp.ok) {
    const errBody = await resp.text();
    if (resp.status === 402) {
      const err = new Error("insufficient_chat_credits") as any;
      err.code = "insufficient_chat_credits";
      throw err;
    }
    throw new Error(`chatWithAIStream failed: ${resp.status} — ${errBody}`);
  }

  // 2. Read just the first NDJSON line to get streamId
  const reader = resp.body?.getReader();
  if (!reader) throw new Error("Response has no body");

  const decoder = new TextDecoder();
  let buffer = "";

  // ── Image / video: read NDJSON directly, no Firestore ──────────────────
  // The backend streams { text }, { media }, { done } lines directly.
  if (params.generationMode === "image" || params.generationMode === "video") {
    const collectedMedia: GeneratedMediaItem[] = [];

    const readAll = async () => {
      try {
        while (true) {
          const { done, value } = await reader.read();
          if (done) break;
          buffer += decoder.decode(value, { stream: true });
          const lines = buffer.split("\n");
          buffer = lines.pop() ?? "";
          for (const line of lines) {
            const trimmed = line.trim();
            if (!trimmed) continue;
            try {
              const data = JSON.parse(trimmed);
              if (data.ping) continue;
              if (data.error) {
                callbacks.onError(data.error);
                return;
              }
              if (typeof data.text === "string") callbacks.onText(data.text);
              if (Array.isArray(data.media)) collectedMedia.push(...data.media);
              if (data.done) {
                callbacks.onDone(
                  data.text || "",
                  data.model || params.model,
                  collectedMedia.length > 0 ? collectedMedia : undefined,
                );
                return;
              }
            } catch {
              /* skip malformed lines */
            }
          }
        }
      } catch (err: any) {
        callbacks.onError(err.message || "Stream read error");
      }
    };

    readAll(); // fire-and-forget; callbacks handle lifetime
    return () => {}; // no Firestore subscription to tear down
  }

  // ── Text mode: read first line for streamId, then subscribe Firestore ──
  let streamId: string | null = null;

  while (true) {
    const { done, value } = await reader.read();
    if (done) break;
    buffer += decoder.decode(value, { stream: true });
    const newlineIdx = buffer.indexOf("\n");
    if (newlineIdx >= 0) {
      const firstLine = buffer.slice(0, newlineIdx).trim();
      reader.releaseLock();
      const data = JSON.parse(firstLine);
      if (!data.streamId) {
        throw new Error("chatWithAIStream response missing streamId");
      }
      streamId = data.streamId;
      break;
    }
  }

  if (!streamId) {
    throw new Error("chatWithAIStream: stream ended before receiving streamId");
  }

  console.log("[chatStream] Got streamId:", streamId);

  // 3. Subscribe to the Firestore doc for real-time updates
  const ref = doc(db, "chatStreams", streamId);
  let snapshotCount = 0;
  let latestMedia: GeneratedMediaItem[] | undefined;
  const unsub = onSnapshot(
    ref,
    (snap) => {
      snapshotCount++;
      if (!snap.exists()) {
        console.warn(
          "[chatStream] Doc does not exist yet, snapshot #",
          snapshotCount,
        );
        return;
      }
      const data = snap.data();
      const text = data.text || "";
      const model = data.model || params.model;
      if (Array.isArray(data.media)) {
        latestMedia = data.media as GeneratedMediaItem[];
      }

      console.log("[chatStream] Snapshot #", snapshotCount, {
        textLen: text.length,
        done: data.done,
        error: data.error,
      });

      if (data.error) {
        callbacks.onError(data.error);
        return;
      }

      callbacks.onText(text);

      if (data.done === true) {
        callbacks.onDone(text, model, latestMedia);
      }
    },
    (err) => {
      console.error("[chatStream] Firestore onSnapshot error:", err);
      callbacks.onError(err.message || "Firestore subscription error");
    },
  );

  return unsub;
}
